import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import AutoStatusToast from '../common/AutoStatusToast';
import ModalDocumentActionButtons from '../common/ModalDocumentActionButtons';
import ProgramSlideCanvas from '../program/ProgramSlideCanvas';
import EvaluationPlanDeleteHoldButton from '../evaluationPlan/EvaluationPlanDeleteHoldButton';
import EvaluationPlanSheetTableCreateModal from '../evaluationPlan/EvaluationPlanSheetTableCreateModal';
import EvaluationPlanSheetTableEditMiniModal from '../evaluationPlan/EvaluationPlanSheetTableEditMiniModal';
import type { ProgramSlideLayoutV1, ProgramSlideTableBlockV1 } from '../../types/programContentSlideCanvas';
import {
  cloneLearningContentWorksheetDocument,
  createDefaultLearningContentWorksheetDocument,
  getWorksheetDocumentPages,
  LEARNING_CONTENT_WORKSHEET_EDITOR_MAX_PAGES,
  LEARNING_CONTENT_WORKSHEET_TITLE,
  normalizeLearningContentWorksheetDocument,
  sheetLayoutSubsetToSlideLayout,
  slideLayoutToSheetLayoutSubset,
  worksheetAiSourceBlockTypes,
  worksheetPageLayoutHasContent,
  type LearningContentWorksheetDocumentV1,
} from '../../types/learningContentWorksheetCanvas';
import { EMPTY_SLIDE_LAYOUT } from '../../types/programContentSlideCanvas';
import {
  EVALUATION_PLAN_SHEET_DEFAULT_TEXT_FONT_SIZE_PX,
  EVALUATION_PLAN_SHEET_HEADER_BAND_NORM,
  type ProgramSlideLayoutSubsetV1,
} from '../../types/evaluationPlanSheetCanvas';
import { useAutoStatusToast } from '../../hooks/useAutoStatusToast';
import { fitWorksheetDocumentToMaxPages } from '../../utils/aiLearningMaterialCanvas';
import {
  clearLearningContentWorksheetDraft,
  loadLearningContentWorksheetDraft,
  purgeLearningWorksheetDraftSavedAtKeys,
} from '../../utils/learningContentWorksheetCanvasDraftStorage';
import { releaseRemovedLearningWorksheetImageUrls } from '../../utils/learningWorksheetImagePendingUpload';
import { createTableBlock, normalizeTableGrid, type TableSelectionRect } from '../../utils/programSlideTableBlock';
import {
  holdModalSavingLabelMinDuration,
  paintModalSaveInProgress,
} from '../../utils/modalSaveButtonIndicator';
import {
  printEvaluationPlanSheetCanvasFromRoot,
  saveEvaluationPlanSheetCanvasAsPdfFromRoot,
} from '../../utils/evaluationPlanSheetCanvasDocumentExport';
import LearningContentWorksheetTemplatePickerModal from './LearningContentWorksheetTemplatePickerModal';
import { createRegistryItemId } from '../../utils/programSlideRegisteredScopeSave';
import {
  cloneWorksheetTemplateLayoutWithNewIds,
  limitProgramWorksheetRegistryNameDraft,
  normalizeProgramWorksheetRegistryName,
  PROGRAM_WORKSHEET_REGISTERED_PAGE_MAX_COUNT,
  programWorksheetRegistryFallbackName,
} from '../../utils/programWorksheetRegistryLimits';
import type { ProgramWorksheetRegisteredPage } from '../../utils/programWorksheetRegisteredPages';
import { isProgramTableCoSaveCancelled } from '../../utils/programAttachmentCoSaveWorkspace';
import './LearningContentWorksheetEditorModal.css';

function sanitizeFileToken(value: string): string {
  return value.trim().replace(/[\\/:*?"<>|]/g, '_').replace(/\s+/g, '_').slice(0, 48);
}

/** 활동지 편집 모달(12060) 위, 페이지 삭제 확인(12070) 아래 */
const LEARNING_WORKSHEET_FLOATING_UI_Z = 12065;

/** 예전 버전이 이 기기에 남긴 초안을 불러왔을 때: 연 직후 상태와 비교하지 않고 '직접 고친 것'으로 본다 */
const RESTORED_OPEN_SNAPSHOT = '__restored__';

/** 닫기 확인: edited = 직접 고친 내용, ai = 손대지 않은 AI 활동지 */
type WorksheetCloseConfirmKind = 'edited' | 'ai';

// 자동 저장(삭제됨)이 남긴 '마지막 임시 저장 시각' 키 정리
purgeLearningWorksheetDraftSavedAtKeys();

/** 변경 여부 비교용 직렬화 (레이아웃 등록이면 페이지 이름·id 포함) */
function serializeWorksheetEditorSnapshot(
  doc: LearningContentWorksheetDocumentV1,
  isRegistry: boolean,
  pageNames: string[],
  pageIds: string[]
): string {
  return isRegistry ? JSON.stringify({ d: doc, n: pageNames, i: pageIds }) : JSON.stringify(doc);
}

function sameWorksheetDocument(
  a: LearningContentWorksheetDocumentV1,
  b: LearningContentWorksheetDocumentV1
): boolean {
  try {
    return (
      JSON.stringify(normalizeLearningContentWorksheetDocument(a)) ===
      JSON.stringify(normalizeLearningContentWorksheetDocument(b))
    );
  } catch {
    return false;
  }
}

function formatSaveClock(at: number): string {
  const d = new Date(at);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function mergeWorksheetLayoutsForImageScan(layouts: ProgramSlideLayoutV1[]): ProgramSlideLayoutV1 {
  return {
    ...EMPTY_SLIDE_LAYOUT,
    images: layouts.flatMap((layout) => layout.images ?? []),
    clipboardImages: layouts.flatMap((layout) => layout.clipboardImages ?? []),
    scanImages: layouts.flatMap((layout) => layout.scanImages ?? []),
  };
}

const LearningContentWorksheetEditorModal: React.FC<{
  open: boolean;
  draftStorageKey?: string;
  workspaceId?: string | null;
  initialDocument?: LearningContentWorksheetDocumentV1 | null;
  /** true면 연 직후를 저장할 변경으로 본다. AI 초안은 저장 전에만 유지된다. */
  initialDirty?: boolean;
  /** editor: 일반 활동지 / registry: 레이아웃 등록 */
  variant?: 'editor' | 'registry';
  initialPageNames?: string[];
  initialPageIds?: string[];
  onClose: () => void;
  /**
   * DB에 바로 저장한다 (저장 버튼·'저장하고 닫기'). 자동 저장은 없다.
   * `options.silent`는 호환용으로만 남아 있고 이 편집기는 넘기지 않는다.
   */
  onSave: (
    document: LearningContentWorksheetDocumentV1,
    meta?: { pageNames: string[]; pageIds: string[] },
    options?: { silent?: boolean }
  ) => void | Promise<void>;
  /** AI 활동지일 때만: 앞 구성의 블록 유형을 피해 새로 만들어 이 편집기에 바로 적용한다 */
  onRegenerateAi?: (
    avoidTypes: string[]
  ) => Promise<{ document: LearningContentWorksheetDocumentV1; warnings: string[] }>;
}> = ({
  open,
  draftStorageKey = '',
  workspaceId = null,
  initialDocument = null,
  initialDirty = false,
  variant = 'editor',
  initialPageNames,
  initialPageIds,
  onClose,
  onSave,
  onRegenerateAi,
}) => {
  const isRegistry = variant === 'registry';
  // 활동지는 최대 2쪽(저장 데이터를 읽을 때의 한도와는 별개)
  const maxPages = isRegistry ? PROGRAM_WORKSHEET_REGISTERED_PAGE_MAX_COUNT : LEARNING_CONTENT_WORKSHEET_EDITOR_MAX_PAGES;
  const [documentDraft, setDocumentDraft] = useState<LearningContentWorksheetDocumentV1 | null>(null);
  /** 마지막으로 DB(또는 연 시점)와 같은 상태의 직렬화. null이면 아직 저장해야 할 상태(AI 초안 등) */
  const [savedSnapshot, setSavedSnapshot] = useState<string | null>(null);
  /** 저장 버튼으로 마지막으로 DB에 저장한 시각 */
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [closeConfirm, setCloseConfirm] = useState<WorksheetCloseConfirmKind | null>(null);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  /** 연 직후(또는 마지막 DB 저장 직후) 상태. 지금과 다르면 교사가 직접 고친 것 */
  const openSnapshotRef = useRef('');
  const [exportBusy, setExportBusy] = useState(false);
  const [activePageIndex, setActivePageIndex] = useState(0);
  const [pendingDeletePageIndex, setPendingDeletePageIndex] = useState<number | null>(null);
  const [pageNamesByIndex, setPageNamesByIndex] = useState<string[]>([]);
  const [pageIdsByIndex, setPageIdsByIndex] = useState<string[]>([]);
  const [nameModal, setNameModal] = useState<{ pageIndex: number; draft: string } | null>(null);
  const [templatePickerPageIndex, setTemplatePickerPageIndex] = useState<number | null>(null);
  const [leftToolbarHost, setLeftToolbarHost] = useState<HTMLElement | null>(null);
  const [rightToolbarHost, setRightToolbarHost] = useState<HTMLElement | null>(null);
  const [tableCreateOpen, setTableCreateOpen] = useState(false);
  const [activeTableId, setActiveTableId] = useState<string | null>(null);
  const [tableSelection, setTableSelection] = useState<TableSelectionRect | null>(null);
  const [tableAnchorRect, setTableAnchorRect] = useState<DOMRect | null>(null);
  const [tableEditOpen, setTableEditOpen] = useState(true);
  const [focusTableId, setFocusTableId] = useState<string | null>(null);
  const [canvasScrollEl, setCanvasScrollEl] = useState<HTMLElement | null>(null);
  const [sheetModalEl, setSheetModalEl] = useState<HTMLElement | null>(null);
  const [sheetModalBounds, setSheetModalBounds] = useState<DOMRect | null>(null);
  const lastSheetModalBoundsKeyRef = useRef('');
  const previousLayoutRef = useRef<ProgramSlideLayoutV1 | null>(null);
  const previousLayoutsRef = useRef<ProgramSlideLayoutV1[]>([]);
  const documentDraftRef = useRef<LearningContentWorksheetDocumentV1 | null>(null);
  const wasOpenRef = useRef(false);
  const pendingRemoteInitialDocumentRef = useRef(false);
  const exportRootRef = useRef<HTMLElement | null>(null);
  const { toastState, showSuccess, showError, showInfo } = useAutoStatusToast(4000);
  const showInfoRef = useRef(showInfo);
  showInfoRef.current = showInfo;
  /** 열 때 2쪽을 넘는 활동지: 손대지 않은 AI 활동지는 2쪽에 다시 맞추고(저장 전까지 원래 데이터는 그대로), 아니면 안내만 */
  const fitOnOpen = useCallback(
    (doc: LearningContentWorksheetDocumentV1): { doc: LearningContentWorksheetDocumentV1; changed: boolean } => {
      if (isRegistry) return { doc, changed: false };
      const fit = fitWorksheetDocumentToMaxPages(normalizeLearningContentWorksheetDocument(doc));
      if (fit.warnings.length) {
        const message = fit.warnings.join(' ');
        window.setTimeout(() => showInfoRef.current(message), 0);
      }
      return fit.changed ? { doc: fit.document, changed: true } : { doc, changed: false };
    },
    [isRegistry]
  );
  const [regenerating, setRegenerating] = useState(false);
  /** 마지막으로 적용한 AI 결과(JSON). 지금 초안과 다르면 교사가 고친 것으로 본다 */
  const lastAiDocJsonRef = useRef('');

  const canvasScrollRef = useCallback((el: HTMLElement | null) => {
    exportRootRef.current = el;
    setCanvasScrollEl(el);
  }, []);

  documentDraftRef.current = documentDraft;

  const syncSheetModalBounds = useCallback(() => {
    if (!sheetModalEl) return;
    const rect = sheetModalEl.getBoundingClientRect();
    const key = `${Math.round(rect.top)}:${Math.round(rect.left)}:${Math.round(rect.width)}:${Math.round(rect.height)}`;
    if (key === lastSheetModalBoundsKeyRef.current) return;
    lastSheetModalBoundsKeyRef.current = key;
    setSheetModalBounds(rect);
  }, [sheetModalEl]);

  useLayoutEffect(() => {
    syncSheetModalBounds();
  }, [syncSheetModalBounds]);

  useEffect(() => {
    if (!sheetModalEl) return;
    const sync = () => syncSheetModalBounds();
    window.addEventListener('resize', sync);
    canvasScrollEl?.addEventListener('scroll', sync, { passive: true });
    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(sync);
      ro.observe(sheetModalEl);
    }
    return () => {
      window.removeEventListener('resize', sync);
      canvasScrollEl?.removeEventListener('scroll', sync);
      ro?.disconnect();
    };
  }, [canvasScrollEl, sheetModalEl, syncSheetModalBounds]);

  /** 기준 문서를 편집기에 올리고, 그 상태의 직렬화를 돌려준다 */
  const applyBaseDocument = useCallback(
    (doc: LearningContentWorksheetDocumentV1): string => {
      const normalized = normalizeLearningContentWorksheetDocument(doc);
      const pages = getWorksheetDocumentPages(normalized);
      const nextDoc = cloneLearningContentWorksheetDocument(normalized);
      setDocumentDraft(nextDoc);
      previousLayoutsRef.current = pages.map((page) => sheetLayoutSubsetToSlideLayout(page));
      previousLayoutRef.current = previousLayoutsRef.current[0] ?? null;
      if (isRegistry) {
        const names = pages.map((_, idx) =>
          normalizeProgramWorksheetRegistryName(
            initialPageNames?.[idx],
            programWorksheetRegistryFallbackName(idx)
          )
        );
        const ids = pages.map((_, idx) => {
          const id = (initialPageIds?.[idx] ?? '').trim();
          return id || createRegistryItemId();
        });
        setPageNamesByIndex(names);
        setPageIdsByIndex(ids);
        return serializeWorksheetEditorSnapshot(nextDoc, true, names, ids);
      }
      setPageNamesByIndex([]);
      setPageIdsByIndex([]);
      return serializeWorksheetEditorSnapshot(nextDoc, false, [], []);
    },
    [isRegistry, initialPageNames, initialPageIds]
  );

  useEffect(() => {
    if (!open) {
      wasOpenRef.current = false;
      pendingRemoteInitialDocumentRef.current = false;
      savingRef.current = false;
      setSaving(false);
      setExportBusy(false);
      setSavedSnapshot(null);
      setSavedAt(null);
      setCloseConfirm(null);
      openSnapshotRef.current = '';
      setDocumentDraft(null);
      previousLayoutRef.current = null;
      previousLayoutsRef.current = [];
      setActivePageIndex(0);
      setPendingDeletePageIndex(null);
      setPageNamesByIndex([]);
      setPageIdsByIndex([]);
      setNameModal(null);
      setTemplatePickerPageIndex(null);
      setRegenerating(false);
      lastAiDocJsonRef.current = '';
      return;
    }

    const justOpened = !wasOpenRef.current;
    wasOpenRef.current = true;
    const draftKey = draftStorageKey.trim();

    /** 기준 문서(DB·대기 저장본)를 올린다. AI 초안·2쪽 맞춤처럼 저장이 필요한 상태면 dirty로 시작 */
    const applyInitial = (doc: LearningContentWorksheetDocumentV1, forceDirty: boolean) => {
      const fitted = fitOnOpen(doc);
      const snapshot = applyBaseDocument(fitted.doc);
      lastAiDocJsonRef.current = fitted.doc.aiSource
        ? JSON.stringify(
            cloneLearningContentWorksheetDocument(normalizeLearningContentWorksheetDocument(fitted.doc)).pages
          )
        : '';
      openSnapshotRef.current = snapshot;
      setSavedSnapshot(forceDirty || fitted.changed ? null : snapshot);
    };

    if (!justOpened) {
      if (pendingRemoteInitialDocumentRef.current && initialDocument) {
        pendingRemoteInitialDocumentRef.current = false;
        setActivePageIndex(0);
        setPendingDeletePageIndex(null);
        applyInitial(initialDocument, false);
      }
      return;
    }

    setActivePageIndex(0);
    setSavedAt(null);
    setCloseConfirm(null);

    /**
     * 예전 버전(로컬 자동 저장)이 이 기기에 남긴 편집 초안 정리:
     * 자동 저장이 없어졌으므로 한 번만 편집기에 불러오고(저장 안 된 상태) 로컬 키는 바로 지운다.
     * AI 초안으로 열 때(initialDirty)는 호출한 쪽이 초안을 관리하므로 건드리지 않는다.
     */
    const leftoverDraft = draftKey && !initialDirty ? loadLearningContentWorksheetDraft(draftKey) : null;
    if (leftoverDraft) {
      clearLearningContentWorksheetDraft(draftKey);
      if (!(initialDocument && sameWorksheetDocument(leftoverDraft, initialDocument))) {
        pendingRemoteInitialDocumentRef.current = false;
        applyBaseDocument(fitOnOpen(leftoverDraft).doc);
        lastAiDocJsonRef.current = '';
        openSnapshotRef.current = RESTORED_OPEN_SNAPSHOT;
        setSavedSnapshot(null);
        window.setTimeout(
          () => showInfoRef.current('이 기기에 남아 있던 저장 안 된 편집 내용을 불러왔어요. 저장해야 반영돼요.'),
          0
        );
        return;
      }
    }

    if (initialDocument) {
      applyInitial(initialDocument, initialDirty);
      pendingRemoteInitialDocumentRef.current = false;
      return;
    }

    pendingRemoteInitialDocumentRef.current = true;
    const snapshot = applyBaseDocument(createDefaultLearningContentWorksheetDocument());
    openSnapshotRef.current = snapshot;
    setSavedSnapshot(snapshot);
  }, [open, initialDocument, initialDirty, draftStorageKey, applyBaseDocument, fitOnOpen]);

  /** 지금 편집 상태의 직렬화 (dirty 판정) */
  const currentSnapshot = useMemo(
    () =>
      documentDraft
        ? serializeWorksheetEditorSnapshot(documentDraft, isRegistry, pageNamesByIndex, pageIdsByIndex)
        : '',
    [documentDraft, isRegistry, pageNamesByIndex, pageIdsByIndex]
  );
  const isDirty = !!documentDraft && (savedSnapshot === null || currentSnapshot !== savedSnapshot);
  /** AI 초안·2쪽 맞춤만으로 dirty인 것과 구분: 교사가 실제로 고쳤는지 */
  const userEdited = !!documentDraft && currentSnapshot !== openSnapshotRef.current;

  const closeWithoutSaving = () => {
    if (savingRef.current) return;
    setCloseConfirm(null);
    onClose();
  };

  /** 닫기·Esc: 저장 안 된 내용이 있으면 확인 모달 */
  const handleRequestClose = () => {
    if (savingRef.current) return;
    if (closeConfirm) {
      setCloseConfirm(null);
      return;
    }
    if (!isDirty) {
      onClose();
      return;
    }
    if (!userEdited) {
      // 직접 고치지 않은 상태: AI 초안이면 저장할지 묻고, 2쪽 맞춤만 된 상태면 저장하지 않고 닫는다
      if (initialDirty && !isRegistry) {
        setCloseConfirm('ai');
        return;
      }
      onClose();
      return;
    }
    setCloseConfirm('edited');
  };

  const handleRegenerateAi = async () => {
    const current = documentDraftRef.current ?? documentDraft;
    if (!onRegenerateAi || !current || regenerating || saving) return;
    const editedSinceAi =
      !lastAiDocJsonRef.current || JSON.stringify(current.pages) !== lastAiDocJsonRef.current;
    if (
      editedSinceAi &&
      !window.confirm('지금 활동지를 새 구성으로 바꿉니다. 직접 고친 내용은 사라집니다. 계속할까요?')
    ) {
      return;
    }
    const avoidTypes = worksheetAiSourceBlockTypes(current);
    setRegenerating(true);
    try {
      const { document: nextRaw, warnings } = await onRegenerateAi(avoidTypes);
      const normalized = normalizeLearningContentWorksheetDocument(nextRaw);
      const pages = getWorksheetDocumentPages(normalized);
      previousLayoutsRef.current = pages.map((page) => sheetLayoutSubsetToSlideLayout(page));
      previousLayoutRef.current = previousLayoutsRef.current[0] ?? null;
      setActivePageIndex(0);
      setPendingDeletePageIndex(null);
      setActiveTableId(null);
      setTableSelection(null);
      const nextDoc = cloneLearningContentWorksheetDocument(normalized);
      setDocumentDraft(nextDoc);
      lastAiDocJsonRef.current = JSON.stringify(nextDoc.pages);
      if (warnings.length) showInfo(warnings[0]);
      else showSuccess('새 구성으로 다시 만들었습니다. 저장해야 반영됩니다.');
    } catch (err) {
      showError(err instanceof Error && err.message ? err.message : '다시 만들지 못했습니다.');
    } finally {
      setRegenerating(false);
    }
  };

  const handleWorksheetTitleChange = (nextTitle: string) => {
    setDocumentDraft((prev) => {
      if (!prev) return prev;
      return normalizeLearningContentWorksheetDocument({ ...prev, title: nextTitle });
    });
  };

  const pageSlideLayouts = useMemo(
    () =>
      documentDraft
        ? getWorksheetDocumentPages(documentDraft).map((page) => sheetLayoutSubsetToSlideLayout(page))
        : [],
    [documentDraft]
  );
  const pageSlideLayoutsRef = useRef(pageSlideLayouts);
  pageSlideLayoutsRef.current = pageSlideLayouts;

  const slideLayout = pageSlideLayouts[activePageIndex] ?? null;

  const activeTable = useMemo(() => {
    if (!slideLayout || !activeTableId) return null;
    return (slideLayout.tables ?? []).find((tbl) => tbl.id === activeTableId) ?? null;
  }, [activeTableId, slideLayout]);

  const handleTableSelectionChange = useCallback((tableId: string | null, selection: TableSelectionRect | null) => {
    setActiveTableId(tableId);
    setTableSelection(selection);
    if (tableId) setTableEditOpen(true);
  }, []);

  const handleTableAnchorRectChange = useCallback((rect: DOMRect | null) => {
    setTableAnchorRect((prev) => {
      if (!rect && !prev) return prev;
      if (!rect || !prev) return rect;
      if (
        Math.round(prev.top) === Math.round(rect.top) &&
        Math.round(prev.left) === Math.round(rect.left) &&
        Math.round(prev.width) === Math.round(rect.width) &&
        Math.round(prev.height) === Math.round(rect.height)
      ) {
        return prev;
      }
      return rect;
    });
  }, []);

  const buildExportFileNameBase = useCallback(() => {
    const title = documentDraft?.title?.trim() || LEARNING_CONTENT_WORKSHEET_TITLE;
    const titleToken = sanitizeFileToken(title);
    const dateLabel = new Date().toLocaleDateString('ko-KR').replace(/\./g, '').trim();
    return `활동지_${titleToken}_${dateLabel}`;
  }, [documentDraft?.title]);

  const handleSavePdf = useCallback(async () => {
    const root = exportRootRef.current;
    if (!root) return;
    setExportBusy(true);
    try {
      await saveEvaluationPlanSheetCanvasAsPdfFromRoot({
        root,
        fileName: `${buildExportFileNameBase()}.pdf`,
      });
    } catch (error) {
      console.error('활동지 PDF 저장 실패:', error);
      alert('PDF 저장에 실패했습니다.');
    } finally {
      setExportBusy(false);
    }
  }, [buildExportFileNameBase]);

  const handlePrint = useCallback(async () => {
    const root = exportRootRef.current;
    if (!root) return;
    setExportBusy(true);
    try {
      const title = documentDraft?.title?.trim() || LEARNING_CONTENT_WORKSHEET_TITLE;
      await printEvaluationPlanSheetCanvasFromRoot(root, title);
    } catch (error) {
      console.error('활동지 프린트 실패:', error);
      alert('프린트에 실패했습니다.');
    } finally {
      setExportBusy(false);
    }
  }, [documentDraft?.title]);

  if (!open || !documentDraft || !slideLayout || pageSlideLayouts.length === 0) return null;

  const patchDocumentPages = (
    updater: (pages: ProgramSlideLayoutSubsetV1[]) => ProgramSlideLayoutSubsetV1[]
  ) => {
    setDocumentDraft((prev) => {
      if (!prev) return prev;
      const currentPages = getWorksheetDocumentPages(prev);
      const nextPages = updater(currentPages);
      return normalizeLearningContentWorksheetDocument({
        ...prev,
        pages: nextPages,
        layout: nextPages[0] ?? createDefaultLearningContentWorksheetDocument().layout,
      });
    });
  };

  const handlePageLayoutChange = (pageIndex: number, next: ProgramSlideLayoutV1) => {
    patchDocumentPages((pages) =>
      pages.map((page, idx) => (idx === pageIndex ? slideLayoutToSheetLayoutSubset(next) : page))
    );
  };

  const handleLayoutChange = (next: ProgramSlideLayoutV1) => {
    handlePageLayoutChange(activePageIndex, next);
  };

  const handleSelectPage = (pageIndex: number) => {
    if (pageIndex === activePageIndex) return;
    setActivePageIndex(pageIndex);
    setActiveTableId(null);
    setTableSelection(null);
    setTableAnchorRect(null);
    setTableEditOpen(false);
  };

  const handleAddPage = () => {
    if (pageSlideLayouts.length >= maxPages) {
      alert(
        isRegistry
          ? `활동지 레이아웃은 최대 ${maxPages}개까지 등록할 수 있습니다.`
          : `활동지는 최대 ${maxPages}페이지까지 추가할 수 있습니다.`
      );
      return;
    }
    const defaultColors = pageSlideLayouts[activePageIndex] ?? pageSlideLayouts[0];
    const emptyLayout = slideLayoutToSheetLayoutSubset({
      ...EMPTY_SLIDE_LAYOUT,
      defaultTextColorHex: defaultColors?.defaultTextColorHex ?? '#111111',
      defaultBackgroundColorHex: defaultColors?.defaultBackgroundColorHex,
      pageBackgroundColorHex: defaultColors?.pageBackgroundColorHex ?? '#ffffff',
    });
    const nextIndex = pageSlideLayouts.length;
    patchDocumentPages((pages) => [...pages, emptyLayout]);
    previousLayoutsRef.current = [...previousLayoutsRef.current, sheetLayoutSubsetToSlideLayout(emptyLayout)];
    if (isRegistry) {
      setPageNamesByIndex((prev) => [
        ...prev,
        programWorksheetRegistryFallbackName(nextIndex),
      ]);
      setPageIdsByIndex((prev) => [...prev, createRegistryItemId()]);
      setNameModal({
        pageIndex: nextIndex,
        draft: programWorksheetRegistryFallbackName(nextIndex),
      });
    }
    setActivePageIndex(nextIndex);
    setActiveTableId(null);
    setTableSelection(null);
    setTableAnchorRect(null);
    setTableEditOpen(false);
  };

  const applyDeletePage = (pageIndex: number) => {
    setPendingDeletePageIndex(null);
    const layouts = pageSlideLayoutsRef.current;
    if (pageIndex < 0 || pageIndex >= layouts.length || layouts.length <= 1) return;

    const deletedLayout = layouts[pageIndex];
    const remainingLayouts = layouts.filter((_, idx) => idx !== pageIndex);
    const ws = (workspaceId ?? '').trim();
    if (ws) {
      void releaseRemovedLearningWorksheetImageUrls(
        deletedLayout,
        mergeWorksheetLayoutsForImageScan(remainingLayouts)
      );
    }

    patchDocumentPages((pages) => pages.filter((_, idx) => idx !== pageIndex));
    previousLayoutsRef.current = previousLayoutsRef.current.filter((_, idx) => idx !== pageIndex);
    if (isRegistry) {
      setPageNamesByIndex((prev) => prev.filter((_, idx) => idx !== pageIndex));
      setPageIdsByIndex((prev) => prev.filter((_, idx) => idx !== pageIndex));
    }

    setActivePageIndex((prev) => {
      if (prev === pageIndex) return Math.max(0, pageIndex - 1);
      if (prev > pageIndex) return prev - 1;
      return prev;
    });
    setActiveTableId(null);
    setTableSelection(null);
    setTableAnchorRect(null);
    setTableEditOpen(false);
  };

  const requestDeletePage = (pageIndex: number) => {
    const layouts = pageSlideLayoutsRef.current;
    const draft = documentDraftRef.current;
    if (pageIndex < 0 || pageIndex >= layouts.length || layouts.length <= 1) return;
    const page = draft ? getWorksheetDocumentPages(draft)[pageIndex] : null;
    if (page && worksheetPageLayoutHasContent(page)) {
      setPendingDeletePageIndex(pageIndex);
      return;
    }
    applyDeletePage(pageIndex);
  };

  const patchTableInLayout = (table: ProgramSlideTableBlockV1, selection?: TableSelectionRect | null) => {
    const normalized = normalizeTableGrid(table);
    handleLayoutChange({
      ...slideLayout,
      tables: (slideLayout.tables ?? []).map((tbl) => (tbl.id === normalized.id ? normalized : tbl)),
    });
    if (selection) {
      setTableSelection(selection);
    }
  };

  const handleCreateTable = (input: { rowCount: number; colCount: number }) => {
    const top = documentDraft.headerBandNorm ?? EVALUATION_PLAN_SHEET_HEADER_BAND_NORM;
    const centerY = (top + 1) / 2;
    const zs = [
      ...slideLayout.boxes.map((b) => b.zIndex),
      ...(slideLayout.tables ?? []).map((t) => t.zIndex),
      ...(slideLayout.shapes ?? []).map((s) => s.zIndex),
    ];
    const zIndex = zs.length ? Math.max(...zs) + 1 : 0;
    const table = createTableBlock({
      rowCount: input.rowCount,
      colCount: input.colCount,
      defaultFontSizePx: EVALUATION_PLAN_SHEET_DEFAULT_TEXT_FONT_SIZE_PX,
      defaultTextColorHex: slideLayout.defaultTextColorHex ?? '#111111',
      x: 0.1,
      y: Math.min(0.72, Math.max(top + 0.02, centerY - 0.18)),
      w: 0.8,
      h: 0.35,
      zIndex,
    });
    handleLayoutChange({
      ...slideLayout,
      tables: [...(slideLayout.tables ?? []), table],
    });
    setTableCreateOpen(false);
    setActiveTableId(table.id);
    setTableEditOpen(true);
    setFocusTableId(table.id);
  };

  const handleDeleteTable = () => {
    if (!activeTableId) return;
    handleLayoutChange({
      ...slideLayout,
      tables: (slideLayout.tables ?? []).filter((tbl) => tbl.id !== activeTableId),
    });
    setActiveTableId(null);
    setTableSelection(null);
    setTableAnchorRect(null);
  };

  const confirmRegistryNameModal = () => {
    if (!nameModal) return;
    const name = normalizeProgramWorksheetRegistryName(
      nameModal.draft,
      programWorksheetRegistryFallbackName(nameModal.pageIndex)
    );
    setPageNamesByIndex((prev) => {
      const next = [...prev];
      while (next.length <= nameModal.pageIndex) {
        next.push(programWorksheetRegistryFallbackName(next.length));
      }
      next[nameModal.pageIndex] = name;
      return next;
    });
    setNameModal(null);
  };

  const applyTemplateToPage = (pageIndex: number, template: ProgramWorksheetRegisteredPage) => {
    const layouts = pageSlideLayoutsRef.current;
    if (pageIndex < 0 || pageIndex >= layouts.length) return;
    const current = documentDraftRef.current
      ? getWorksheetDocumentPages(documentDraftRef.current)[pageIndex]
      : null;
    if (current && worksheetPageLayoutHasContent(current)) {
      const ok = window.confirm(
        `${pageIndex + 1}페이지에 이미 내용이 있습니다. 선택한 레이아웃으로 바꿀까요?`
      );
      if (!ok) return;
    }
    const nextLayout = cloneWorksheetTemplateLayoutWithNewIds(template.layout);
    patchDocumentPages((pages) => pages.map((page, idx) => (idx === pageIndex ? nextLayout : page)));
    previousLayoutsRef.current = previousLayoutsRef.current.map((layout, idx) =>
      idx === pageIndex ? sheetLayoutSubsetToSlideLayout(nextLayout) : layout
    );
    setTemplatePickerPageIndex(null);
    setActiveTableId(null);
    setTableSelection(null);
    setTableAnchorRect(null);
    setTableEditOpen(false);
  };

  /** DB 저장 (저장 버튼·'저장하고 닫기' 공용). 성공하면 true */
  const persistToDb = async (): Promise<boolean> => {
    if (savingRef.current) return false;
    const sourceDoc = documentDraftRef.current;
    if (!sourceDoc) return false;
    const ws = (workspaceId ?? '').trim();
    if (!ws) {
      alert('워크스페이스 정보가 없어 저장할 수 없습니다.');
      return false;
    }
    savingRef.current = true;
    setSaving(true);
    const saveStartedAt = Date.now();
    await paintModalSaveInProgress();
    try {
      const nextPages = getWorksheetDocumentPages(sourceDoc).map((page) =>
        slideLayoutToSheetLayoutSubset(sheetLayoutSubsetToSlideLayout(page))
      );
      const nextDocument = normalizeLearningContentWorksheetDocument({
        ...sourceDoc,
        title: isRegistry
          ? '활동지 레이아웃'
          : sourceDoc.title.trim() || LEARNING_CONTENT_WORKSHEET_TITLE,
        pages: nextPages,
        layout: nextPages[0],
      });
      const meta = isRegistry
        ? {
            pageNames: nextPages.map((_, idx) =>
              normalizeProgramWorksheetRegistryName(
                pageNamesByIndex[idx],
                programWorksheetRegistryFallbackName(idx)
              )
            ),
            pageIds: nextPages.map((_, idx) => {
              const id = (pageIdsByIndex[idx] ?? '').trim();
              return id || createRegistryItemId();
            }),
          }
        : undefined;
      await onSave(nextDocument, meta);
      const savedDoc = cloneLearningContentWorksheetDocument(nextDocument);
      const savedSnap = serializeWorksheetEditorSnapshot(
        savedDoc,
        isRegistry,
        meta?.pageNames ?? [],
        meta?.pageIds ?? []
      );
      // 저장하는 동안 더 고쳤으면 그 편집은 그대로 두고(계속 dirty) 기준만 바꾼다
      if (documentDraftRef.current === sourceDoc) {
        if (draftStorageKey.trim()) clearLearningContentWorksheetDraft(draftStorageKey);
        previousLayoutsRef.current = nextPages.map((page) => sheetLayoutSubsetToSlideLayout(page));
        previousLayoutRef.current = previousLayoutsRef.current[0] ?? null;
        if (JSON.stringify(sourceDoc) !== JSON.stringify(savedDoc)) setDocumentDraft(savedDoc);
        if (meta) {
          setPageNamesByIndex(meta.pageNames);
          setPageIdsByIndex(meta.pageIds);
        }
        openSnapshotRef.current = savedSnap;
      }
      setSavedSnapshot(savedSnap);
      setSavedAt(Date.now());
      return true;
    } catch (error) {
      if (isProgramTableCoSaveCancelled(error)) return false;
      console.error(isRegistry ? '활동지 레이아웃 저장 실패:' : '활동지 저장 실패:', error);
      showError('저장하지 못했어요. 다시 시도해 주세요.');
      return false;
    } finally {
      await holdModalSavingLabelMinDuration(saveStartedAt);
      savingRef.current = false;
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (savingRef.current || !isDirty) return;
    await persistToDb();
  };

  /** 확인 모달 [저장하고 닫기]: 실패하면 편집기를 닫지 않는다(토스트는 persistToDb가 띄움) */
  const handleSaveAndClose = async () => {
    if (savingRef.current) return;
    const ok = await persistToDb();
    setCloseConfirm(null);
    if (ok) onClose();
  };

  const saveStatusText = savedAt != null ? `저장됨 · ${formatSaveClock(savedAt)}` : '';

  const worksheetContentBounds = {
    top: documentDraft.headerBandNorm ?? EVALUATION_PLAN_SHEET_HEADER_BAND_NORM,
    bottom: 1,
  };
  const showWorksheetPageBadges = pageSlideLayouts.length > 1;
  const canDeleteWorksheetPage = pageSlideLayouts.length > 1;

  return createPortal(
    <>
      <div
        className="cp-modal-backdrop cp-evaluation-plan-web-doc-backdrop cp-evaluation-plan-sheet-canvas-backdrop cp-learning-worksheet-editor-backdrop"
        role="presentation"
        onKeyDown={(e) => {
          if (e.key === 'Escape' && pendingDeletePageIndex == null) void handleRequestClose();
        }}
      >
        <div
          ref={setSheetModalEl}
          className="cp-modal cp-evaluation-plan-web-doc-modal cp-evaluation-plan-sheet-canvas-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="cp-learning-worksheet-editor-title"
          data-tutorial-ws="editor-modal"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="cp-modal-document-header cp-evaluation-plan-web-doc-viewer-header">
            <h3 id="cp-learning-worksheet-editor-title">
              {isRegistry ? '활동지 레이아웃 등록' : `${LEARNING_CONTENT_WORKSHEET_TITLE} A4 캔버스 작성`}
            </h3>
            {onRegenerateAi && !isRegistry && documentDraft?.aiSource ? (
              <button
                type="button"
                className="cp-learning-worksheet-regen-btn"
                onClick={() => void handleRegenerateAi()}
                disabled={regenerating || saving || exportBusy}
                aria-busy={regenerating}
                title="앞에서 쓴 활동 유형을 피해서 새로 만듭니다"
              >
                {regenerating ? '다시 만드는 중…' : '다른 구성으로 다시 만들기'}
              </button>
            ) : null}
            <ModalDocumentActionButtons
              onSavePdf={() => void handleSavePdf()}
              onPrint={() => void handlePrint()}
              busy={exportBusy || saving || regenerating}
            />
          </div>

          <div className="cp-evaluation-plan-web-doc-full-bleed-line" aria-hidden="true" />

          <div className="cp-modal-body cp-evaluation-plan-sheet-canvas-body">
            <div className="cp-program-slide-root cp-program-slide-root--sheet-editor" data-slide-surface="light">
              <div className="cp-evaluation-plan-sheet-canvas-workspace">
                <div
                  ref={setLeftToolbarHost}
                  className="cp-evaluation-plan-sheet-canvas-toolbar-host cp-evaluation-plan-sheet-canvas-toolbar-host--left"
                  data-tutorial-ws="toolbar-left"
                  aria-hidden={!leftToolbarHost}
                />
                <div ref={canvasScrollRef} className="cp-evaluation-plan-sheet-canvas-scroll">
                  <div className="cp-learning-worksheet-page-stack">
                    {pageSlideLayouts.map((pageLayout, pageIndex) => {
                      const isActive = pageIndex === activePageIndex;
                      return (
                        <div
                          key={`worksheet-page-${pageIndex}`}
                          className={`cp-learning-worksheet-page-frame${isActive ? ' is-active' : ''}`}
                        >
                          <div className="cp-learning-worksheet-page-main">
                            <button
                              type="button"
                              className="cp-learning-worksheet-page-focus-hit"
                              onClick={() => handleSelectPage(pageIndex)}
                              aria-label={`${pageIndex + 1}페이지 선택`}
                            />
                            <div className="cp-evaluation-plan-sheet-canvas-a4-page">
                              <ProgramSlideCanvas
                              layout={pageLayout}
                              onLayoutChange={(next) => handlePageLayoutChange(pageIndex, next)}
                              readOnly={!isActive}
                              toolbarPreset="evaluation-sheet"
                              canvasAspectRatio="a4"
                              imageStorageKind="learning-worksheet"
                              evaluationSheetWorkspaceId={workspaceId}
                              externalToolbarLeftHost={isActive ? leftToolbarHost : null}
                              externalToolbarRightHost={isActive ? rightToolbarHost : null}
                              positionSyncRoot={canvasScrollEl}
                              viewportContainerRect={sheetModalBounds}
                              floatedToolbarZIndex={LEARNING_WORKSHEET_FLOATING_UI_Z}
                              fixedHeaderBand={{
                                title: documentDraft.title,
                                academicYearLabel: documentDraft.academicYearLabel,
                                pageNumber: showWorksheetPageBadges ? pageIndex + 1 : undefined,
                                titleEditable: isActive && pageIndex === 0,
                                onTitleChange: pageIndex === 0 ? handleWorksheetTitleChange : undefined,
                              }}
                              contentBoundsNorm={worksheetContentBounds}
                              defaultTextColorHex={pageLayout.defaultTextColorHex}
                              canvasBackgroundColorHex={pageLayout.pageBackgroundColorHex}
                              onRequestCreateTable={
                                isActive ? () => setTableCreateOpen(true) : undefined
                              }
                              onTableSelectionChange={
                                isActive ? handleTableSelectionChange : undefined
                              }
                              onTableAnchorRectChange={
                                isActive ? handleTableAnchorRectChange : undefined
                              }
                              focusTableId={isActive ? focusTableId : null}
                              onFocusTableHandled={isActive ? () => setFocusTableId(null) : undefined}
                              syncedTableCellSelection={
                                isActive && activeTableId && tableSelection
                                  ? { tableId: activeTableId, ...tableSelection }
                                  : null
                              }
                            />
                          </div>
                          </div>
                          {isRegistry ? (
                            <div className="cp-learning-worksheet-page-actions">
                              <button
                                type="button"
                                className="cp-learning-worksheet-page-chip-btn cp-learning-worksheet-page-name"
                                onClick={() =>
                                  setNameModal({
                                    pageIndex,
                                    draft:
                                      pageNamesByIndex[pageIndex] ??
                                      programWorksheetRegistryFallbackName(pageIndex),
                                  })
                                }
                                title="레이아웃 이름"
                              >
                                이름 ·{' '}
                                {pageNamesByIndex[pageIndex] ??
                                  programWorksheetRegistryFallbackName(pageIndex)}
                              </button>
                              {canDeleteWorksheetPage ? (
                                <EvaluationPlanDeleteHoldButton
                                  itemId={`worksheet-page-${pageIndex}`}
                                  className="cp-learning-worksheet-page-chip-btn cp-learning-worksheet-page-delete"
                                  label="페이지 삭제"
                                  onRemove={() => requestDeletePage(pageIndex)}
                                />
                              ) : null}
                            </div>
                          ) : (
                            <div className="cp-learning-worksheet-page-actions">
                              {canDeleteWorksheetPage ? (
                                <EvaluationPlanDeleteHoldButton
                                  itemId={`worksheet-page-${pageIndex}`}
                                  className="cp-learning-worksheet-page-chip-btn cp-learning-worksheet-page-delete"
                                  label="페이지 삭제"
                                  onRemove={() => requestDeletePage(pageIndex)}
                                />
                              ) : null}
                              <button
                                type="button"
                                className="cp-learning-worksheet-page-chip-btn cp-learning-worksheet-page-apply-template"
                                onClick={() => setTemplatePickerPageIndex(pageIndex)}
                                title="활동지 레이아웃 적용하기"
                              >
                                활동지 레이아웃 적용하기
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                    <button
                      type="button"
                      className="cp-learning-worksheet-page-chip-btn cp-learning-worksheet-page-add"
                      data-tutorial-ws="add-page"
                      onClick={handleAddPage}
                      disabled={pageSlideLayouts.length >= maxPages}
                      aria-label="페이지 추가"
                      title={
                        pageSlideLayouts.length >= maxPages
                          ? `최대 ${maxPages}페이지까지 추가할 수 있습니다.`
                          : '페이지 추가'
                      }
                    >
                      <span className="cp-learning-worksheet-page-add-icon" aria-hidden>
                        +
                      </span>
                      <span className="cp-learning-worksheet-page-add-label">페이지 추가</span>
                    </button>
                  </div>
                </div>
                <div
                  ref={setRightToolbarHost}
                  className="cp-evaluation-plan-sheet-canvas-toolbar-host cp-evaluation-plan-sheet-canvas-toolbar-host--right"
                  aria-hidden={!rightToolbarHost}
                />
              </div>
            </div>
          </div>

          <div className="cp-evaluation-plan-web-doc-full-bleed-line" aria-hidden="true" />

          <div className="cp-modal-footer cp-evaluation-plan-web-doc-modal-footer">
            {saveStatusText ? (
              <span className="cp-learning-worksheet-save-status" role="status" title="저장 버튼으로 DB에 저장한 시각">
                {saveStatusText}
              </span>
            ) : null}
            <button
              type="button"
              className="cp-btn-secondary"
              onClick={() => void handleRequestClose()}
              disabled={saving}
            >
              닫기
            </button>
            <button
              type="button"
              className={`cp-btn-primary cp-learning-worksheet-save-btn ${
                isDirty || saving ? 'cp-learning-worksheet-save-btn--dirty' : 'cp-learning-worksheet-save-btn--clean'
              }`}
              onClick={() => void handleSave()}
              disabled={saving || !isDirty}
              aria-live="polite"
              title={!isDirty && !saving ? '저장된 상태예요.' : undefined}
            >
              {saving ? '저장 중…' : isDirty ? '저장' : '저장됨 ✓'}
            </button>
          </div>
        </div>
      </div>
      <EvaluationPlanSheetTableCreateModal
        open={tableCreateOpen}
        backdropZIndex={LEARNING_WORKSHEET_FLOATING_UI_Z}
        onClose={() => setTableCreateOpen(false)}
        onSubmit={handleCreateTable}
      />
      <EvaluationPlanSheetTableEditMiniModal
        open={!!activeTableId && !!activeTable && tableEditOpen}
        table={activeTable}
        selection={tableSelection}
        anchorRect={tableAnchorRect}
        containerRect={sheetModalBounds}
        panelZIndex={LEARNING_WORKSHEET_FLOATING_UI_Z}
        onClose={() => setTableEditOpen(false)}
        onPatchTable={patchTableInLayout}
        onDeleteTable={handleDeleteTable}
        onToast={showSuccess}
      />
      <AutoStatusToast state={toastState} />

      {!isRegistry ? (
        <LearningContentWorksheetTemplatePickerModal
          open={templatePickerPageIndex != null}
          workspaceId={(workspaceId ?? '').trim()}
          onClose={() => setTemplatePickerPageIndex(null)}
          onPick={(page) => {
            if (templatePickerPageIndex == null) return;
            applyTemplateToPage(templatePickerPageIndex, page);
          }}
        />
      ) : null}

      {nameModal ? (
        <div
          className="cp-modal-backdrop cp-learning-worksheet-page-name-backdrop"
          style={{ zIndex: LEARNING_WORKSHEET_FLOATING_UI_Z + 5 }}
          role="presentation"
          onClick={() => setNameModal(null)}
        >
          <div
            className="cp-modal cp-learning-worksheet-page-name-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cp-learning-worksheet-page-name-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="cp-learning-worksheet-page-name-title">레이아웃 이름</h3>
            <div className="cp-modal-body">
              <label className="cp-learning-worksheet-page-name-label" htmlFor="cp-learning-worksheet-page-name-input">
                {nameModal.pageIndex + 1}페이지 레이아웃 이름
              </label>
              <input
                id="cp-learning-worksheet-page-name-input"
                className="cp-input"
                value={nameModal.draft}
                maxLength={12}
                autoFocus
                onChange={(e) =>
                  setNameModal((prev) =>
                    prev
                      ? { ...prev, draft: limitProgramWorksheetRegistryNameDraft(e.target.value) }
                      : prev
                  )
                }
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    confirmRegistryNameModal();
                  }
                }}
              />
            </div>
            <div className="cp-modal-footer">
              <button type="button" className="cp-btn-secondary" onClick={() => setNameModal(null)}>
                취소
              </button>
              <button type="button" className="cp-btn-primary" onClick={confirmRegistryNameModal}>
                확인
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {closeConfirm ? (
        <div
          className="cp-modal-backdrop cp-learning-worksheet-close-confirm-backdrop"
          style={{ zIndex: LEARNING_WORKSHEET_FLOATING_UI_Z + 5 }}
          role="presentation"
          onClick={() => {
            if (!saving) setCloseConfirm(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.stopPropagation();
              if (!saving) setCloseConfirm(null);
            }
          }}
        >
          <div
            className="cp-modal cp-learning-worksheet-close-confirm-modal"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="cp-learning-worksheet-close-confirm-title"
            aria-describedby="cp-learning-worksheet-close-confirm-lead"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="cp-learning-worksheet-close-confirm-title">
              {closeConfirm === 'ai' ? 'AI 활동지 저장' : '저장하지 않은 변경'}
            </h3>
            <div className="cp-modal-body">
              <p id="cp-learning-worksheet-close-confirm-lead" className="cp-learning-worksheet-close-confirm-lead">
                {closeConfirm === 'ai'
                  ? 'AI가 만든 활동지를 아직 저장하지 않았어요.'
                  : '저장하지 않은 변경 내용이 있어요.'}
              </p>
              <p className="cp-learning-worksheet-close-confirm-meta">
                {closeConfirm === 'ai'
                  ? '저장하지 않고 닫으면 이 활동지는 사라져요.'
                  : '저장하지 않고 닫으면 고친 내용이 사라져요.'}
              </p>
            </div>
            <div className="cp-modal-footer cp-learning-worksheet-close-confirm-footer">
              <button
                type="button"
                className="cp-btn-secondary"
                onClick={() => setCloseConfirm(null)}
                disabled={saving}
              >
                취소
              </button>
              <button
                type="button"
                className="cp-btn-secondary cp-learning-worksheet-close-confirm-discard"
                onClick={closeWithoutSaving}
                disabled={saving}
              >
                저장하지 않고 닫기
              </button>
              <button
                type="button"
                className="cp-btn-primary cp-learning-worksheet-close-confirm-save"
                onClick={() => void handleSaveAndClose()}
                disabled={saving}
                autoFocus
              >
                {saving ? '저장 중…' : '저장하고 닫기'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {pendingDeletePageIndex != null ? (
        <div
          className="cp-modal-backdrop cp-evaluation-plan-web-doc-delete-backdrop cp-learning-worksheet-page-delete-backdrop"
          role="presentation"
          onClick={() => setPendingDeletePageIndex(null)}
        >
          <div
            className="cp-modal cp-evaluation-plan-web-doc-delete-modal"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="cp-learning-worksheet-page-delete-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="cp-learning-worksheet-page-delete-title">페이지 삭제</h3>
            <div className="cp-modal-body">
              <p className="cp-evaluation-plan-web-doc-delete-lead">
                {pendingDeletePageIndex + 1}페이지에 입력된 내용이 있습니다. 이 페이지를 삭제하시겠습니까?
              </p>
            </div>
            <div className="cp-modal-footer">
              <button type="button" className="cp-btn-secondary" onClick={() => setPendingDeletePageIndex(null)}>
                취소
              </button>
              <button
                type="button"
                className="cp-btn-primary"
                onClick={() => applyDeletePage(pendingDeletePageIndex)}
              >
                삭제
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>,
    document.body
  );
};

export default LearningContentWorksheetEditorModal;
